/* eslint-disable no-case-declarations */
import { shade } from "polished";
import { type FC, type ReactElement } from "react";

import { getCssColor } from "@/app-utils/color.ts";
import { VARIANT_TYPE } from "@/settings/settings";

export interface IVariantSymbolProps {
  type: string;
  width: number;
  offset?: number;
  enlarge?: number;
  omitSVG?: boolean;
  styleContent?: any;
  groupColor?: string;
  groupQuantity?: number;
}

// TODO will be used only as standalone svg element, not in tracks (remove groups, omitSVG, enlarge, ...)
const VariantSymbol: FC<IVariantSymbolProps> = (props) => {
  const type: string = props.type.toLowerCase();

  const symbolWidth: number = props.width;
  const halfSymbolWidth: number = Math.round(symbolWidth / 2);
  const fifthSymbolWidth: number = Math.round(symbolWidth / 5);

  const enlarge: number = props.enlarge || 0;
  const offset: number = props.offset !== undefined && props.offset !== null ? props.offset : halfSymbolWidth;
  const omitSVG: boolean = props.omitSVG || false;

  let symbolElement: ReactElement = null;

  switch (type) {
    case VARIANT_TYPE.POINT:
      const pnt1 = `${offset - halfSymbolWidth - enlarge},${halfSymbolWidth}`;
      const pnt2 = `${offset},${-enlarge}`;
      const pnt3 = `${offset + halfSymbolWidth + enlarge},${halfSymbolWidth}`;
      const pnt4 = `${offset},${symbolWidth + enlarge}`;
      symbolElement = (
        <polygon className="variant-symbol" style={props.styleContent} points={`${pnt1} ${pnt2} ${pnt3} ${pnt4}`} />
      );
      break;

    case VARIANT_TYPE.DELETION:
      const del1 = `${offset},${-enlarge}`;
      const del2 = `${offset + halfSymbolWidth + enlarge},${symbolWidth + enlarge}`;
      const del3 = `${offset - halfSymbolWidth - enlarge},${symbolWidth + enlarge}`;
      symbolElement = (
        <polygon className="variant-symbol" style={props.styleContent} points={`${del1} ${del2} ${del3}`} />
      );
      break;

    case VARIANT_TYPE.INSERTION:
      const ins1 = `${offset - halfSymbolWidth},${-enlarge}`;
      const ins2 = `${offset + halfSymbolWidth},${-enlarge}`;
      const ins3 = `${offset},${symbolWidth + enlarge}`;
      symbolElement = (
        <polygon className="variant-symbol" style={props.styleContent} points={`${ins1} ${ins2} ${ins3}`} />
      );
      break;

    case VARIANT_TYPE.INDEL:
      const ind1 = `${offset - halfSymbolWidth - enlarge},${-enlarge}`;
      const ind2 = `${offset + halfSymbolWidth + enlarge},${-enlarge}`;
      const ind3 = `${offset + fifthSymbolWidth + enlarge},${halfSymbolWidth}`;
      const ind4 = `${offset + halfSymbolWidth + enlarge},${symbolWidth + enlarge}`;
      const ind5 = `${offset - halfSymbolWidth - enlarge},${symbolWidth + enlarge}`;
      const ind6 = `${offset - fifthSymbolWidth - enlarge},${halfSymbolWidth}`;
      symbolElement = (
        <polygon
          className="variant-symbol"
          style={props.styleContent}
          points={`${ind1} ${ind2} ${ind3} ${ind4} ${ind5} ${ind6}`}
        />
      );
      break;

    case VARIANT_TYPE.TRANSLOCATION_DELETION:
    case VARIANT_TYPE.TRANSLOCATION_INSERTION:
      symbolElement = (
        <circle
          className="variant-symbol"
          style={props.styleContent}
          cx={offset}
          cy={halfSymbolWidth}
          r={halfSymbolWidth - 1}
        />
      );
      break;
  }

  if (props.groupColor) {
    const color = getCssColor(props.groupColor);
    const bgColor = shade(0.5, color);
    let plusSymbol: ReactElement;

    if (props.groupQuantity && props.groupQuantity > 1) {
      plusSymbol = (
        <>
          <rect x={offset + 3.5} y={halfSymbolWidth - 7.5} width={3} height={1} fill={"white"} strokeWidth={0} />
          <rect x={offset + 4.5} y={halfSymbolWidth - 8.5} width={1} height={3} fill={"white"} strokeWidth={0} />
        </>
      );
    }

    symbolElement = (
      <g>
        {symbolElement}
        <circle cx={offset + 5} cy={halfSymbolWidth - 7} r={3} fill={color} strokeWidth={1} stroke={bgColor} />
        {plusSymbol}
        );
      </g>
    );
  }

  if (omitSVG) {
    return symbolElement;
  }

  return (
    <svg width={props.width} height={props.width} className={`variant variant-${type}`} style={{ display: "inline" }}>
      {symbolElement}
    </svg>
  );
};

export { VariantSymbol };
